/**
 * AKTools (AKShare HTTP) service
 * Uses a Tauri-side HTTP proxy when available (to avoid CORS),
 * otherwise falls back to direct HTTP (or Vite dev proxy).
 */
import axios from 'axios';
import type { RequestConfig } from '@/types/api';
import { isTauri, safeInvoke } from '@/utils/tauri';
import { createApiError, categorizeHttpError } from './errors';

export type AktoolsProxyHttpResponse = {
  status: number;
  headers: Record<string, string>;
  body: string;
};

/** 内置默认 Aktools 地址，未配置时使用 */
export const BUILTIN_AKTOOLS_BASE_URL = 'https://price.hyc-tech.top';

function getDefaultBaseUrl(): string {
  const isDev = !!(import.meta as any)?.env?.DEV;
  if (isDev) return '/aktools';
  return BUILTIN_AKTOOLS_BASE_URL;
}

function getBaseUrl(): string {
  // 优先从 localStorage 读取用户配置的 base URL；用户配置优先于内置默认
  try {
    const settingsStorage = localStorage.getItem('settings-storage');
    if (settingsStorage) {
      const settings = JSON.parse(settingsStorage);
      const userUrl = settings?.state?.aktoolsBaseUrl;
      if (userUrl && String(userUrl).trim() !== '') {
        return String(userUrl).trim().replace(/\/+$/, '');
      }
    }
  } catch {
    // 忽略解析错误
  }

  // Tauri / 生产环境：环境变量 > 内置默认；开发环境 getDefaultBaseUrl() 为 /aktools
  const envUrl =
    (import.meta as any)?.env?.VITE_AKTOOLS_BASE_URL ||
    (globalThis as any)?.process?.env?.VITE_AKTOOLS_BASE_URL;
  if (envUrl && String(envUrl).trim() !== '') {
    return String(envUrl).trim().replace(/\/+$/, '');
  }
  return getDefaultBaseUrl();
}

function buildUrl(
  baseUrl: string,
  path: string,
  params?: Record<string, unknown>
): string {
  const normalizedBase = baseUrl.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (normalizedBase.startsWith('/')) {
    const urlPath = `${normalizedBase}${normalizedPath}`;
    const url = new URL(urlPath, window.location.origin);
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null) continue;
        url.searchParams.set(k, String(v));
      }
    }
    return url.toString();
  }

  const url = new URL(normalizedPath, normalizedBase);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function sleep(ms: number): Promise<void> {
  await new Promise(r => setTimeout(r, ms));
}

export class AktoolsService {
  private baseUrl = getBaseUrl();
  private client = axios.create({
    baseURL: this.baseUrl,
    timeout: 30000,
  });

  getBaseUrl(): string {
    // 每次获取时重新读取，以支持动态更新
    const newBaseUrl = getBaseUrl();
    if (newBaseUrl !== this.baseUrl) {
      this.baseUrl = newBaseUrl;
      this.client = axios.create({
        baseURL: this.baseUrl,
        timeout: 30000,
      });
    }
    return this.baseUrl;
  }

  setBaseUrl(url: string | null): void {
    // 若为 null 表示使用内置默认，不依赖 localStorage 写入时机
    const newBaseUrl = url ? url.replace(/\/+$/, '') : getDefaultBaseUrl();
    this.baseUrl = newBaseUrl;
    this.client = axios.create({
      baseURL: this.baseUrl,
      timeout: 30000,
    });
  }

  /**
   * Perform GET and parse JSON response.
   * Retries once on transient server/network failures.
   */
  async getJson<T>(
    path: string,
    params?: Record<string, unknown>,
    config?: RequestConfig
  ): Promise<{
    data: T;
    sourceUrl: string;
    status: number;
    headers: Record<string, string>;
  }> {
    // 检查 baseUrl 是否已配置
    if (!this.baseUrl || this.baseUrl.trim() === '') {
      throw createApiError(
        'VALIDATION_ERROR',
        'Aktools base URL is not configured. Please configure it in Settings.',
        { provider: 'aktools' }
      );
    }
    const sourceUrl = buildUrl(this.baseUrl, path, params);
    const isBrowser = typeof window !== 'undefined';
    const isLocalhost =
      isBrowser &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1');
    const shouldUseProxy =
      !isTauri() && isLocalhost && this.baseUrl.startsWith('http');
    const requestBaseUrl = shouldUseProxy ? '/aktools' : this.baseUrl;
    const requestUrl = buildUrl(requestBaseUrl, path, params);
    const maxAttempts = 2;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Prefer native (Rust-side) HTTP in Tauri to avoid CORS.
        if (isTauri()) {
          const res = await safeInvoke<AktoolsProxyHttpResponse>(
            'proxy_http_get',
            {
              url: sourceUrl,
              headers: { accept: 'application/json' },
            }
          );
          if (!res) {
            throw createApiError(
              'NETWORK_ERROR',
              'Tauri HTTP proxy unavailable',
              { provider: 'aktools' }
            );
          }
          const status = res.status;
          const headers = res.headers || {};
          const body = res.body || '';

          if (status < 200 || status >= 300) {
            // Some AKTools endpoints return JSON { error: ... } with non-200
            let msg = body;
            try {
              const j = JSON.parse(body);
              msg =
                j && typeof j === 'object' && 'error' in j
                  ? String((j as any).error)
                  : body;
            } catch {
              // ignore
            }
            throw createApiError(
              categorizeHttpError(status, msg),
              msg || `HTTP ${status}`,
              {
                provider: 'aktools',
              }
            );
          }

          const parsed = JSON.parse(body) as T;
          return { data: parsed, sourceUrl, status, headers };
        }

        // Browser / Node fallback (dev proxy or CORS-enabled server)
        const requestConfig: {
          params?: Record<string, unknown>;
          signal?: AbortSignal;
          timeout?: number;
        } = {};
        if (params && !shouldUseProxy) {
          requestConfig.params = params;
        }
        if (config?.signal) {
          requestConfig.signal = config.signal;
        }
        if (config?.timeout !== undefined) {
          requestConfig.timeout = config.timeout;
        }
        const r = shouldUseProxy
          ? await axios.get(requestUrl, requestConfig)
          : await this.client.get(path, requestConfig);

        // axios already throws for non-2xx
        return {
          data: r.data as T,
          sourceUrl,
          status: r.status,
          headers: (r.headers || {}) as Record<string, string>,
        };
      } catch (e: any) {
        const isLast = attempt === maxAttempts;

        // In axios errors: e.response?.status exists
        const status = e?.response?.status;
        const isTransient =
          e?.code === 'ECONNRESET' ||
          e?.code === 'ETIMEDOUT' ||
          e?.name === 'AbortError' ||
          e?.message?.includes('timeout') ||
          status === 500 ||
          status === 502 ||
          status === 503 ||
          status === 504;

        if (!isLast && isTransient) {
          await sleep(250 * attempt);
          continue;
        }
        throw e;
      }
    }

    // Unreachable
    throw createApiError('UNKNOWN_ERROR', 'AKTools request failed', {
      provider: 'aktools',
    });
  }

  async getVersion(
    config?: RequestConfig
  ): Promise<{ ak_current_version: string; at_current_version: string }> {
    const res = await this.getJson<any>('/version', undefined, config);
    return res.data;
  }
}

export const aktoolsService = new AktoolsService();
